import { IResult, UAParser } from 'ua-parser-js';

export const clearUrlParams = () => {
  if (typeof window === 'undefined') return;

  const currentUrl = window.location.href;
  const url = new URL(currentUrl);

  // Check if there are search parameters
  if (url.searchParams.toString()) {
    // Clear all search parameters
    url.search = '';

    // Update the URL without causing a page refresh
    window.history.replaceState({}, '', url.toString());
  }
};

export const HttpStatusCodes = {
  OK: 200,
  CREATED: 201,
  ACCEPTED: 202,
  NO_CONTENT: 204,
  BAD_REQUEST: 400,
  UNAUTHORIZED: 401,
  FORBIDDEN: 403,
  NOT_FOUND: 404,
  CONFLICT: 409,
  INTERNAL_SERVER_ERROR: 500,
  SERVICE_UNAVAILABLE: 503,
};

// Check if the code is running on the client side
export const isClient = typeof window !== 'undefined';

// Broadcast when the PWA Safari install guide is closed, so the banner and the
// homepage tile can hide in sync (both surfaces listen for this event).
export const PWA_GUIDE_CLOSED_EVENT = 'pwa-guide-closed';

// Get user agent information
export const getUserAgent = (): IResult | null => {
  if (isClient) {
    const parser = new UAParser();
    return parser.getResult();
  }
  return null;
};

// Get an item from local storage
export const getLocalStorageItem = (key: string): string | null => {
  if (isClient) {
    return localStorage.getItem(key);
  }
  return null;
};

// Set an item in local storage
export const setLocalStorageItem = (key: string, value: string) => {
  if (isClient) {
    localStorage.setItem(key, value);
  }
};

export const replaceNullWithQuotesJson = (obj: {
  [key: string]: any;
}): { [key: string]: string } => {
  Object.keys(obj).forEach((key: string) => {
    if (obj[key] == null) {
      obj[key] = '';
    }
  });
  return obj;
};

export const allowedHostsInRichText = [
  'player.vimeo.com',
  'player.vimeo.com/embed',
  'www.youtube.com',
  'youtube.com',
  'youtube.com/',
  'youtu.be',
];

// Helper function to sort by priority
export const sortByPriority = (a: any, b: any) => {
  const aPriority = a?.priority ?? 50;
  const bPriority = b?.priority ?? 50;

  const getPriorityBucket = (value: number) => {
    if (value == null || value === 50) {
      return 1;
    }
    return value < 50 ? 0 : 2;
  };

  const aBucket = getPriorityBucket(aPriority);
  const bBucket = getPriorityBucket(bPriority);

  if (aBucket !== bBucket) {
    return aBucket - bBucket;
  }

  return aPriority - bPriority;
};

export const getLocale = (langCode: string, fallback = 'en-US') => {
  const localeMap: { [key: string]: string } = {
    en: 'en-US',
    pt: 'pt-PT',
    es: 'es-ES',
    fr: 'fr-FR',
    it: 'it-IT',
    pl: 'pl-PL',
  };

  return localeMap[langCode] || fallback;
};

export const checkIfValidUser = (response = {}) => {
  //error handling
  const responseKeys = Object.keys(response);
  return responseKeys.length > 0 && !responseKeys.includes('message');
};

// Retry logic
// const rr = await retryWithBackoff(() => fetchData(actions?.payload), 3, 300, config?.successStatusCodes);

interface RetryOptions {
  retries?: number;
  baseDelay?: number;
  maxDelay?: number;
  successCodes?: number[];
  errorCodes?: number[];
  onRetry?: (error: unknown, attempt: number, delay: number) => void;
}

export const retryWithBackoff = async <T>(
  fn: () => Promise<T>,
  options: RetryOptions = {},
): Promise<T> => {
  const {
    retries = 3,
    baseDelay = 500,
    maxDelay = 5000,
    successCodes,
    errorCodes,
    onRetry,
  } = options;

  let lastError: unknown;

  for (let attempt = 0; attempt < retries; attempt++) {
    try {
      const result = await fn();

      // Check if result is a Response
      if (result instanceof Response) {
        const status = result.status;

        // Check if it's a success
        const isSuccess =
          result.ok || (successCodes && successCodes.includes(status));

        // Check if we should retry this error
        const shouldRetry = errorCodes && errorCodes.includes(status);

        if (!isSuccess) {
          if (shouldRetry && attempt < retries - 1) {
            throw new Error(`HTTP ${status}: ${result.statusText}`);
          }

          // Non-success but not configured to retry
          console.warn(`Received status ${status}, not retrying`);
        }
      }

      return result; // Success!
    } catch (error) {
      lastError = error;

      // Don't wait if this was the last attempt
      if (attempt === retries - 1) {
        break;
      }

      // Calculate delay with exponential backoff
      const delay = Math.min(baseDelay * Math.pow(2, attempt), maxDelay);

      // Notify about retry
      if (onRetry) {
        onRetry(error, attempt + 1, delay);
      } else {
        console.warn(
          `Attempt ${attempt + 1}/${retries} failed. Retrying in ${delay}ms...`,
          error instanceof Error ? error.message : error,
        );
      }

      // Wait before next retry
      await new Promise((resolve) => setTimeout(resolve, delay));
    }
  }

  // All retries exhausted
  throw lastError || new Error('Failed after max retries');
};

// Generic Contentful Pagination
type PaginatedCollection<T> = {
  total: number;
  items: T[];
};

type ExtractCollectionFn<T> = (
  result: any,
) => PaginatedCollection<T> | undefined;

export const fetchPaginated = async <T>(
  client: any,
  query: any,
  extractCollection: ExtractCollectionFn<T>,
  params: any,
  limit = 100,
): Promise<T[]> => {
  let skip = 0;
  let total = Infinity;
  const all: T[] = [];

  while (skip < total) {
    const res = await client.query({
      query,
      variables: { ...params, limit, skip },
      fetchPolicy: 'no-cache',
      errorPolicy: 'all',
    });

    const collection = extractCollection(res);

    if (!collection) break;

    all.push(...collection.items);
    total = collection.total;
    skip += limit;
  }

  return all;
};

export function withTimeout<T>(
  promise: Promise<T>,
  ms = 8000,
  message = 'Timeout',
) {
  return new Promise<T>((resolve, reject) => {
    const timer = setTimeout(() => reject(new Error(message)), ms);
    promise.then(
      (v) => {
        clearTimeout(timer);
        resolve(v);
      },
      (err) => {
        clearTimeout(timer);
        reject(err);
      },
    );
  });
}

export const getWhatsAppNumber = (mpId: string) => {
  const whatsAppNumbers: Record<string, number> = {
    SHC576: 34657942190,
    SHC574: 351967084444,
    SHC589: 351924115500,
    SHC575: 34628078340,
  };
  return whatsAppNumbers[mpId] || null;
};

export const getKeyValutSecretBydomain = (domain: string) => {
  const firstListOfPreviewDomains: string[] = ['localhost:3001'];
  const firstListOfDomains: string[] = [
    'localhost:3001',
    'localhost:3002',
    'localhost:3003',
    'livat.com',
    'www.livat.com',
    // 'livli.eu',
    // 'www.livli.eu',
    'livli.com',
    'www.livli.com',
    // 'i-huset.se',
    // 'www.i-huset.se',
    // 'birstacity.se',
    // 'www.birstacity.se',
    'avionshopping.se',
    'www.avionshopping.se',
    // 'erikslundshoppingcenter.se',
    // 'www.erikslundshoppingcenter.se',
    'almhultshandelsplats.se',
    'www.almhultshandelsplats.se',
    'livealmhult.se',
    'www.livealmhult.se',
    'skendeshopping.pl',
    'www.skendeshopping.pl',
    'churchillsquare.com',
    'www.churchillsquare.com',
    'italiedeux.com',
    'www.italiedeux.com',
    'alejabielany.pl',
    'www.alejabielany.pl',
    'wolapark.pl',
    'www.wolapark.pl',
    'marshopping.com',
    'www.marshopping.com',
  ];
  // const secondListOfDomains: string[] =[];

  if (firstListOfDomains?.includes(domain) && !domain?.includes('preview')) {
    return process.env.KV_SECRET_NAME || process.env.NEXT_PUBLIC_KV_SECRET_NAME;
  } else if (
    !firstListOfDomains?.includes(domain) &&
    !domain?.includes('preview')
  ) {
    return (
      process.env.KV_SECOND_SECRET_NAME ||
      process.env.NEXT_PUBLIC_KV_SECOND_SECRET_NAME
    );
  } else {
    return process.env.KV_SECRET_NAME || process.env.NEXT_PUBLIC_KV_SECRET_NAME;
  }
};

export function escapeXml(value: string) {
  return value
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&apos;');
}

export const getLocalizedOtherText = (locale: string): string => {
  const localizedText = {
    en: 'Other',
    cs: 'Další',
    fr: 'Autre',
    it: 'Altro',
    pl: 'Inne',
    pt: 'Outro',
    sk: 'Iné',
    es: 'Otros',
    sv: 'Övriga',
  };
  return (
    localizedText[locale as keyof typeof localizedText] ?? localizedText.en
  );
};

export const isBookingSubdomain = (url?: string): boolean => {
  if (!url) return false;
  return /^(https?:\/\/)?((qa|dev)\.)?booking\.[^.]+\.com(\/.*)?$/.test(url);
};

// Zones that keep their URL path prefix in local development.
export const PREFIX_ENABLED_ZONES = ['matosinhos', 'algarve'];

// Resolves the site path prefix: used as-is in prod; in development only for
// prefix-enabled zones (matosinhos/algarve), otherwise empty.
export const resolveSitePathPrefix = (sitePathPrefix?: string): string =>
  process.env.NEXT_PUBLIC_ENV === 'development' &&
  !PREFIX_ENABLED_ZONES.includes(sitePathPrefix ?? '')
    ? ''
    : sitePathPrefix || '';
